import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import AccountInfo from '../components/SignUp/AccountInfo'
import PersonalInfo from '../components/SignUp/PersonalInfo'
import CourtsSelection from '../components/SignUp/CourtsSelection'
import AddressDetails from '../components/SignUp/AddressDetails'
import authApiService from '../services/authServices'
import { config } from '../config/config';
import Modal from '../utils/modalUtils';
import AppearenceIcon from '../components/SignUp/AppearenceIcon';
import useLoading from '../hooks/useLoading';
import { ThreeDots } from 'react-loader-spinner';


const SignUp = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const [userId, setUserId] = useState(null);
  const [loaderText, setLoaderText] = useState('Creating your account...');

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    first_name: '',
    confirmPassword: '',
    last_name: '',
    email: '',
    court_address: '',
    home_phone: '',
    cell_phone: '',
    preferred_phone: '',
    password_hint: '',
    registered_city: JSON.parse(localStorage.getItem("selectedCity"))?.id,
    referral: '',
    icon: '',
    last_login: '',
    account_enabled: 'Y',
    account_expired: 'N',
    account_locked: 'N',
    credentials_expired: 'N',
    current_season: '1',
    current_wins: '0',
    current_losses: '0',
    current_ties: '0',
    total_wins: '0',
    total_losses: '0',
    total_ties: '0',
    best_result: '0',
    best_result_season: '1',
    low_score: 0,
    seasons_entered: '0',
    player_level: '',
    ratedby: '',
    plays: '',
    playing_preference: '',
    opentochallenges: '',
    playwithonlysamegender: '',
    opponent_skilllevel: '',
    racket: '',
    tennisstring: '',
    shoes: '',
    favoritePlayer: '',
    home_court: '',
    court_city: '',
    court_state: '',
    court_id: '',
    court_lighted: '',
    numberOfCourts: '',
    openCourtMeridiem: '',
    openCourtHour: '',
    closeCourtMeridiem: '',
    closeCourtHour: '',
    court_verified: '',
    address: '',
    city: '',
    province: '',
    postal_code: '',
    country: '',
    date_joined: new Date().toISOString().split('T')[0],
    male: true
  });

  const navigate = useNavigate();
  const { isLoading, withLoading } = useLoading();

  const normalizePlayerLevelForSelect = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const raw = String(value).trim();
    if (raw === "") return "";
    if (raw.includes(".")) return raw;

    const num = Number(raw);
    if (Number.isNaN(num)) return raw;
    return Number.isInteger(num) ? `${num.toFixed(1)}` : String(num);
  };

  // Ensure registered_city (membership) is always from selectedCity - never from address
  useEffect(() => {
    const selectedCity = JSON.parse(localStorage.getItem("selectedCity"));
    if (selectedCity?.id && !isEditMode) {
      setFormData(prev => ({ ...prev, registered_city: selectedCity.id }));
    }
  }, [isEditMode]);

  // Check if user came from editprofile URL and fetch user data
  useEffect(() => {
    const checkEditMode = async () => {
      // Check if URL contains editprofile or if there's a userId parameter
      const isEditProfile = location.pathname.includes('editprofile') || searchParams.get('userId');
      const playerId = searchParams.get('userId');
      
      if (isEditProfile && playerId) {
        setIsEditMode(true);
        setUserId(playerId);
        
        try {
          console.log('Fetching user data for edit mode, userId:', playerId);
          const response = await authApiService.get(`user/${playerId}`);
          
          if (response && response.data) {
            console.log('User data fetched:', response.data);
            // Populate form with existing user data
            setFormData(prevData => ({
              ...prevData,
              ...response.data,
              player_level: normalizePlayerLevelForSelect(response.data.player_level),
              // Keep some fields as they were
              password: '', // Don't populate password
              confirmPassword: '', // Don't populate confirm password
            }));
          }
        } catch (error) {
          console.error('Error fetching user data for edit:', error);
          Modal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to load user data for editing',
            confirmButtonColor: '#4fa94d'
          });
        }
      }
    };

    checkEditMode();
  }, [location.pathname, searchParams]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const formatPhone = (phone) => {
    const vPhone = phone.replace(/[^\d]/g, '');
    let vFMPhone = '';

    for (let j = 0; j < vPhone.length; j++) {
      if (j === 0) {
        vFMPhone += `(${vPhone.charAt(j)}`;
      } else if (j === 2) {
        vFMPhone += `${vPhone.charAt(j)})`;
      } else if (j === 3 || j === 6) {
        vFMPhone += ` ${vPhone.charAt(j)}`;
      } else {
        vFMPhone += vPhone.charAt(j);
      }
    }
    return vFMPhone;
  };

  const validateField = (fieldName) => {
    switch (fieldName) {
      case 'username':
        if (!formData.username) return "Username is required";
        break;
      case 'password':
        if (!formData.password) return "Password is required";
        break;
      case 'confirmPassword':
        if (!formData.confirmPassword) return "Confirm Password is required";
        if (formData.password !== formData.confirmPassword) 
          return "Password and Confirm Password must match";
        break;
      case 'first_name':
        if (!formData.first_name) return "First Name is required";
        break;
      case 'last_name':
        if (!formData.last_name) return "Last Name is required";
        break;
      case 'email':
        if (!formData.email) return "Email is required";
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) return "Please enter a valid email address";
        break;
      case 'address':
        if (!formData.address) return "Street address is required";
        break;
      case 'city':
        if (!formData.city) return "City is required";
        break;
      case 'province':
        if (!formData.province) return "State is required";
        break;
      case 'postal_code':
        if (!formData.postal_code) return "Zip Code is required";
        break;
      case 'country':
        if (!formData.country) return "Country is required";
        break;
      case 'password_hint':
        if (!formData.password_hint) return "Password Hint is required";
        break;
      case 'referral':
        if (!formData.referral) return "How did you find us?";
        break;
      case 'player_level':
        if (!formData.player_level) return "Player Level is required";
        break;
      case 'playing_preference':
        if (formData.playing_preference.length === 0) return "Playing Preference is required";
        break;
      case 'icon':
        if (!formData.icon) return "Please select an appearance icon";
        break;
      case 'court_id':
        // If home_court is filled, it means user is adding new court directly (no dropdown)
        // So court_id validation should be skipped in that case
        if (!formData.home_court && !formData.court_id) {
          return "Please select a court";
        }
        break;
      case 'opponent_skilllevel':
        if (!formData.opponent_skilllevel && formData.opentochallenges === 'yes') 
          return "Please select opponent skill level";
        break;
      case 'home_phone':
        if (formData.home_phone) {
          const formattedHomePhone = formatPhone(formData.home_phone);
          if (formattedHomePhone.length !== 14) 
            return "Home Phone should be in (xxx) xxx xxxx format";
        }
        break;
      case 'cell_phone':
        if (formData.cell_phone) {
          const formattedCellPhone = formatPhone(formData.cell_phone);
          if (formattedCellPhone.length !== 14) 
            return "Cell Phone should be in (xxx) xxx xxxx format";
        }
        break;
      default:
        return null;
    }
    return null;
  };

  const validateForm = () => {
    const fieldOrder = isEditMode
      ? ['first_name', 'last_name', 'email', 'address', 'city', 'province', 'postal_code', 'country', 'referral', 'player_level', 'playing_preference', 'icon', 'court_id', 'opponent_skilllevel', 'home_phone', 'cell_phone']
      : [
      'username',
      'password',
      'confirmPassword',
      'first_name',
      'last_name',
      'email',
      'address',
      'city',
      'province',
      'postal_code',
      'country',
      'password_hint',
      'referral',
      'player_level',
      'playing_preference',
      'icon',
      'court_id',
      'opponent_skilllevel',
      'home_phone',
      'cell_phone'
    ];

    for (const field of fieldOrder) {
      const error = validateField(field);
      if (error) {
        return { field, message: error };
      }
    }

    return null;
  };

  const handleDelete = async () => {
    if (!isEditMode || !userId) {
      Modal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Delete option is only available in edit mode',
        confirmButtonColor: '#4fa94d'
      });
      return;
    }

    const result = await Modal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      setLoaderText('Deleting your profile...');
      await withLoading(async () => {
        try {
          const response = await authApiService.put(`deleteUser?id=${userId}`);
          
          if (response.code === 200 || response.code === 201) {
            Modal.fire({
              icon: 'success',
              title: 'Deleted!',
              text: 'User has been deleted successfully.',
              timer: 2000,
              showConfirmButton: false
            }).then(() => {
              navigate('/');
              window.location.reload();
            });
          }
        } catch (error) {
          console.error('Error deleting user:', error);
          Modal.fire({
            icon: 'error',
            title: 'Error',
            text: 'Failed to delete user. Please try again.',
            confirmButtonColor: '#4fa94d'
          });
        }
      });
    }
  };

  const handleSignUp = async () => {
    const error = validateForm();
    if (error) {
      const fieldLabels = {
        username: 'Username',
        password: 'Password',
        confirmPassword: 'Confirm Password',
        first_name: 'First Name',
        last_name: 'Last Name',
        email: 'Email',
        address: 'Street Address',
        city: 'City',
        province: 'State',
        postal_code: 'Zip Code',
        country: 'Country',
        password_hint: 'Password Hint',
        referral: 'How Did You Find Us',
        player_level: 'Player Level',
        playing_preference: 'Playing Preference',
        icon: 'Appearance Icon',
        court_id: 'Court',
        opponent_skilllevel: 'Opponent Skill Level',
        home_phone: 'Home Phone',
        cell_phone: 'Cell Phone'
      };

      Modal.fire({
        title: `${fieldLabels[error.field]}`,
        text: error.message,
        icon: 'warning',
        confirmButtonText: 'OK'
      });

      // Focus on the field with error if possible
      const element = document.getElementsByName(error.field)[0];
      if (element) {
        element.focus();
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      
      return;
    }

    if (isEditMode && userId) {
      setLoaderText('Updating your profile...');
    } else {
      setLoaderText('Creating your account...');
    }
    
    await withLoading(async () => {
      try {
        let response;
        
        if (isEditMode && userId) {
          // Update existing user - exclude restricted fields (username, password, password_hint)
          const { username, password, confirmPassword, password_hint, ...updateData } = formData;
          response = await authApiService.put(`updateUser?id=${userId}`, updateData);
        } else {
          // Create new user - ALWAYS use selectedCity (dropdown) for membership, never address city
          const selectedCity = JSON.parse(localStorage.getItem("selectedCity"));
          const membershipCityId = selectedCity?.id ?? formData.registered_city;
          const signUpPayload = {
            ...formData,
            registered_city: membershipCityId,
          };
          response = await authApiService.post('sign-up', signUpPayload);
        }

        if (response.code === 200 || response.code === 201) {
          localStorage.setItem("signupData", JSON.stringify(response.data));
          
          // Auto-login after signup: Save token if available
          if (!isEditMode && response.data.accessToken) {
            localStorage.setItem('accessToken', response.data.accessToken);
            window.dispatchEvent(new Event('authChange'));
            console.log('Token saved after signup, user auto-logged in');
          }
          
          if (isEditMode) {
            Modal.fire({
              icon: 'success',
              title: 'Success!',
              text: 'Profile updated successfully!',
              showConfirmButton: false,
              timer: 2000,
              timerProgressBar: true,
              allowOutsideClick: false,
              allowEscapeKey: false
            }).then(() => {
              navigate(`/carter/profile?userId=${userId}`);
            });
          } else {
            // Signup flow: no success popup, go directly to registration success page.
            navigate(`/carter/signupregistrationsuccess?first_name=${encodeURIComponent(formData.first_name)}&last_name=${encodeURIComponent(formData.last_name)}`);
          }
        }
      } catch (error) {
        console.error('Error during sign up/update:', error);
        Modal.fire({
          icon: 'error',
          title: 'Oops...',
          text: error.response?.data?.message || 'Something went wrong! Please try again.',
          confirmButtonText: 'OK',
          confirmButtonColor: '#4fa94d'
        });
      }
    });
  };

  return (
    <>
      <div className="page-container col-sm-9" style={{ position: 'relative' }}>
        {isLoading && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            borderRadius: '8px'
          }}>
            <div style={{ textAlign: 'center' }}>
              <ThreeDots
                height="80"
                width="80"
                radius="9"
                color="#4fa94d"
                ariaLabel="three-dots-loading"
                wrapperStyle={{}}
                wrapperClassName=""
                visible={true}
              />
              <p style={{ 
                marginTop: '20px', 
                color: '#4fa94d', 
                fontSize: '18px',
                fontWeight: '600'
              }}>
                {loaderText}
              </p>
            </div>
          </div>
        )}
        
        <div className="card shadow-sm grey-box editerForm p-0">
          <div className='card-body'>
            <div className="row" id="edituser">
              <AccountInfo formData={formData} handleInputChange={handleInputChange} isLoading={isLoading} isEditMode={isEditMode} />
              <PersonalInfo formData={formData} handleInputChange={handleInputChange} isLoading={isLoading} />
              <AddressDetails formData={formData} handleInputChange={handleInputChange} isLoading={isLoading} />
              <CourtsSelection formData={formData} handleInputChange={handleInputChange} isLoading={isLoading} isEditMode={isEditMode} />
              <AppearenceIcon formData={formData} handleInputChange={handleInputChange} isLoading={isLoading} />
            </div>

            <div className="col-12 mb-5">
              <div className="buttons">
                {isEditMode && (
                  <div className="left red-btn">
                    <div className="buttonwrapper">
                      <button 
                        className="squarebutton" 
                        onClick={handleDelete}
                        disabled={isLoading}
                        style={{ 
                          background: 'none', 
                          border: 'none', 
                          padding: 0,
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                          opacity: isLoading ? 0.7 : 1,
                          color: '#d33'
                        }}
                      >
                        <span>Delete</span>
                      </button>
                    </div>
                  </div>
                )}
                <div className="right green-btn">
                  <div className="buttonwrapper">
                    <button 
                      className="squarebutton" 
                      onClick={handleSignUp}
                      disabled={isLoading}
                      style={{ 
                        background: 'none', 
                        border: 'none', 
                        padding: 0,
                        cursor: isLoading ? 'not-allowed' : 'pointer',
                        opacity: isLoading ? 0.7 : 1
                      }}
                    >
                      <span>{isEditMode ? 'Update' : 'Save'}</span>
                    </button>
                  </div>
                </div>
                <div className="right white-btn">
                  <div className="buttonwrapper">
                    <a href="/carter/profile" className="squarebutton" style={{ pointerEvents: isLoading ? 'none' : 'auto', opacity: isLoading ? 0.7 : 1 }}>
                      <span>Cancel</span>
                    </a>
                  </div>
                </div>

                <input id="bCancel" type="hidden" name="bCancel" value="false" />
                <input id="bDelete" type="hidden" name="bDelete" value="false" />
                <div className="clear"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

export default SignUp